import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { TwoFactorChallenge } from '@nucleus-commerce/api-client/platform';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { toChallengeCode } from '../api/mappers';
import { useCountdown } from '../hooks/use-countdown';
import { useVerifyTwoFactor } from '../hooks/use-verify-two-factor';
import {
  formatCountdown,
  verifyFailure,
  type ChallengeEnd,
  type CodeMethod,
} from '../schemas/two-factor-rules';
import {
  authenticatorCodeSchema,
  recoveryCodeSchema,
  type CodeValues,
} from '../schemas/two-factor-schemas';

import { OneTimeCodeInput } from './one-time-code-input';
import { SupportContact } from './support-contact';

const CODE_FIELD: ReadonlySet<string> = new Set(['code']);

export interface TwoFactorStepProps {
  challenge: TwoFactorChallenge;
  /** Runs after the session cookie is set and /me is fresh. */
  onVerified: () => void | Promise<void>;
  /** Leaves the step, e.g. back to the password form. */
  onBack: () => void;
  /** The challenge ran out; the caller restarts sign-in and explains why. */
  onExpired: (reason: ChallengeEnd) => void;
  backLabel: string;
}

function CodeForm({
  challenge,
  method,
  onVerified,
  onExpired,
}: Pick<TwoFactorStepProps, 'challenge' | 'onVerified' | 'onExpired'> & { method: CodeMethod }) {
  const { t } = useTranslation('auth');
  const message = useErrorMessage();
  const verify = useVerifyTwoFactor();
  const form = useForm<CodeValues>({
    resolver: useI18nResolver(method === 'totp' ? authenticatorCodeSchema : recoveryCodeSchema),
    defaultValues: { code: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await verify.mutateAsync({
        challengeToken: challenge.challengeToken,
        code: toChallengeCode(values.code, method),
      });
    } catch (error) {
      const failure = verifyFailure(error);
      if (failure === 'expired' || failure === 'invalid') {
        onExpired(failure);
      } else if (failure === 'wrong_code') {
        form.setError(
          'code',
          { type: 'server', message: t(`twoFactor.wrongCode.${method}`) },
          { shouldFocus: true },
        );
      } else if (!applyFieldErrors(error, form.setError, { knownFields: CODE_FIELD })) {
        form.setError('root.server', { type: 'server', message: message(error) });
      }
      return;
    }
    await onVerified();
  });

  const rootError = form.formState.errors.root?.server?.message;

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        <p className="text-muted-foreground text-sm">{t(`twoFactor.instructions.${method}`)}</p>
        {rootError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{rootError}</AlertDescription>
          </Alert>
        ) : null}
        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t(`twoFactor.label.${method}`)}</FormLabel>
              <FormControl>
                <OneTimeCodeInput method={method} autoFocus {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" size="lg" className="h-11 w-full" disabled={verify.isPending}>
          {verify.isPending ? t('twoFactor.verifying') : t('twoFactor.verify')}
        </Button>
      </form>
    </Form>
  );
}

/**
 * Second step of a sign-in that answered 202: an authenticator code, or a recovery code when the
 * challenge allows one. The challenge lives five minutes; the countdown shows it and hands back
 * to the caller when it runs out.
 */
export function TwoFactorStep({
  challenge,
  onVerified,
  onBack,
  onExpired,
  backLabel,
}: TwoFactorStepProps) {
  const { t } = useTranslation('auth');
  const [method, setMethod] = useState<CodeMethod>('totp');
  const secondsLeft = useCountdown(challenge.expiresAt);
  const expired = secondsLeft === 0;
  const allowsRecovery = challenge.methods.includes('recovery_code');
  const other: CodeMethod = method === 'totp' ? 'recovery_code' : 'totp';

  useEffect(() => {
    if (expired) onExpired('expired');
  }, [expired, onExpired]);

  return (
    <div className="flex flex-col gap-4">
      <CodeForm
        key={method}
        challenge={challenge}
        method={method}
        onVerified={onVerified}
        onExpired={onExpired}
      />
      <p className="text-muted-foreground text-center text-xs" data-testid="two-factor-countdown">
        {t('twoFactor.expiresIn', { time: formatCountdown(secondsLeft) })}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button type="button" variant="link" size="sm" className="px-0" onClick={onBack}>
          {backLabel}
        </Button>
        {allowsRecovery ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="px-0"
            onClick={() => setMethod(other)}
          >
            {t(`twoFactor.switchTo.${other}`)}
          </Button>
        ) : null}
      </div>
      <SupportContact variant="twoFactor" />
    </div>
  );
}
